import { BetsServiceContextProvider, Platform } from '@sport1/bets-service-client'
import NonFlexingContainer from '@sport1/news-styleguide/NonFlexingContainer'
import { LabelProps, LayoutProps, TagProps } from '@sport1/types/web'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { FC, PropsWithChildren, useCallback, useMemo } from 'react'
import Button from '@sport1/news-styleguide/Button'
import Grid from '@sport1/react-elements/Grid'
import { spacings } from '@sport1/news-styleguide/spacings'
import colorPattern from '@sport1/news-styleguide/colorPattern'
import useTheme from '@sport1/react-elements/useTheme'
import { ThemeProps } from '@sport1/news-styleguide/Types'
import ArticleBottomNavigation from './ArticleBottomNavigation'
import { useAdPlacementContext } from '@/context/AdPlacementContext/AdPlacementProvider'
import { useMobile } from '@/hooks/useMobile'
import BottomNavigation from '@/layouts/Standard/BottomNavigation'
import Header from '@/layouts/Standard/Header/Header'
import PageContentLayoutFallback from '@/layouts/Standard/PageContentLayout/Fallback'
import Config from '@/utils/Config'
import { NavigationProvider } from '@/utils/navigation/Standard'
import { NavigationData } from '@/utils/navigation/Standard/types/NavigationItem'
import DisplaySkyAd from '@/components/Ads/AdSlot/DisplaySkyAd'
import { getValueByBreakpoint, BreakPointProps } from '@/helpers/breakpoint'
import { BreakPointDisplaySizes } from '@/helpers/pageHelper'
import { SizeTypes } from '@/types/size'
import { MAX_CONTENT_WIDTH } from '@/hooks/useSpaceAfterContent/constants'
const InactiveHomePopup = dynamic(() => import('@/components/InactiveHomePopup'), { ssr: false })

type StandardLayoutProps = {
    navigation?: NavigationData
    tagNavigation?: TagProps | LabelProps
    competitions?: (TagProps | LabelProps)[]
    isAdsActive?: boolean // to disable ads, need to pass 'false', otherwise ads enabled
    disableBillboard?: boolean // next to all ads are active we sometimes need to disable only the billboard
    noBack2Home?: boolean // provided by 404 page to not display back 2 home footer
    mainContenMaxWidth?: number
    showArticleToolbar?: boolean
    recommendedArticles?: LayoutProps['bottomStickyComponents']
}

const Footer = dynamic(() => import('@/layouts/Standard/Footer/Footer'))

const StandardLayout: FC<StandardLayoutProps & PropsWithChildren> = ({
    navigation,
    competitions,
    children,
    isAdsActive = true,
    disableBillboard = false,
    noBack2Home = false,
    mainContenMaxWidth,
    showArticleToolbar = false,
    recommendedArticles,
}) => {
    const { skyScraper } = useAdPlacementContext()
    const { isMobile } = useMobile()
    const router = useRouter()
    const isHomePage = router.pathname === '/'
    const isHighlights = router.asPath?.endsWith('/highlights')
    const isNewsticker = router.asPath?.endsWith('/news')
    const isMediathekPage = router.pathname.startsWith('/tv-video/')
    const showBack2Home = !isHomePage && !noBack2Home
    const theme = useTheme()
    const { surfaceColors } = (useTheme() as ThemeProps['theme']) || {}

    const isDesktop = useMemo(
        () =>
            getValueByBreakpoint(BreakPointDisplaySizes, theme as BreakPointProps) ===
            SizeTypes.DESKTOP,
        [theme]
    )

    const predicateBgColor = useCallback(() => {
        if (isMediathekPage) return colorPattern.onyx
        if ((isDesktop && isHighlights) || (isDesktop && isNewsticker)) return colorPattern.pearl
        return colorPattern.ghost
    }, [isDesktop, isHighlights, isNewsticker, isMediathekPage])

    return (
        <div className="relative w-full" data-testid="s1-page-wrapper">
            <BetsServiceContextProvider
                env={Config.BETS_SERVICE_ENV}
                platform={isMobile ? Platform.MOBILE : Platform.DESKTOP}
            >
                <NavigationProvider navigation={navigation}>
                    <InactiveHomePopup />
                    <NonFlexingContainer
                        width="100%"
                        centerVertical
                        className={isMediathekPage ? '' : 's1-content-wrapper'}
                    >
                        {!isMediathekPage && isAdsActive && (
                            <div className="s1-sky-left-wrapper">
                                {skyScraper?.placementId && (
                                    <DisplaySkyAd
                                        placement="sky"
                                        placementId="sky2"
                                        isLeftSky
                                        visibleBreadCrumbs={!!navigation?.tag}
                                    />
                                )}
                            </div>
                        )}
                        <NonFlexingContainer
                            maxWidth={isMediathekPage ? undefined : MAX_CONTENT_WIDTH}
                            className={
                                isMediathekPage
                                    ? 's1-centered'
                                    : 's1-centered s1-main-content-wrapper'
                            }
                            backgroundColor={surfaceColors?.['background-tertiary']}
                        >
                            <Header
                                competitions={competitions}
                                disableBillboard={disableBillboard}
                            />
                            <NonFlexingContainer
                                className={
                                    isMediathekPage
                                        ? `s1-main-content gap-[36px] mtmd:gap-[64px] px-3`
                                        : 's1-main-content'
                                }
                                id="s1-main-content"
                                position="relative"
                                zIndex={10}
                                maxWidth={mainContenMaxWidth || 1113}
                                width="100%"
                                marginX="auto"
                                backgroundColor={predicateBgColor()}
                            >
                                {children || <PageContentLayoutFallback />}
                                {showArticleToolbar ? (
                                    <ArticleBottomNavigation
                                        recommendedArticles={recommendedArticles}
                                    />
                                ) : (
                                    <BottomNavigation />
                                )}
                                {showBack2Home && (
                                    <Grid
                                        centerHorizontal
                                        testID="button-wrapper"
                                        marginTop={spacings['spacing-none']}
                                        marginBottom={
                                            isMediathekPage
                                                ? [
                                                      spacings['spacing-11'],
                                                      spacings['spacing-11'],
                                                      spacings['spacing-14'],
                                                  ]
                                                : spacings['spacing-11']
                                        }
                                    >
                                        <Button
                                            buttonType="M"
                                            buttonVariant="tertiary"
                                            text="Zur Startseite"
                                            href={isMediathekPage ? '/tv-video' : '/?b2h=true'}
                                            paddingY="spacing-5"
                                            paddingX="spacing-9"
                                        />
                                    </Grid>
                                )}
                            </NonFlexingContainer>
                            <Footer />
                        </NonFlexingContainer>
                        {!isMediathekPage && isAdsActive && (
                            <div className="s1-sky-right-wrapper">
                                {skyScraper?.placementId && (
                                    <DisplaySkyAd
                                        {...skyScraper}
                                        visibleBreadCrumbs={!!navigation?.tag}
                                    />
                                )}
                            </div>
                        )}
                    </NonFlexingContainer>
                </NavigationProvider>
            </BetsServiceContextProvider>
        </div>
    )
}

export default StandardLayout
